// ===========================================
// Auto Generate: what a run did, or what Check first expects it to do (#376)
// ===========================================
// ONE component for both, so a check and the run that follows it read the same way. Sections, in
// order: invoiced (or expected) · "To finish this run, fix these" (each fix naming the families it
// holds back) · "* Do these by hand" · children not billed (#190) · quiet lines for families already
// invoiced and with nothing to bill · real failures. Plan md/plan/auto-generate-partial-batch.md §5.

import { useState } from 'react';
import { Alert, Box, Button, Link, List, ListItem, ListItemText, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ClassPricingLink from './ClassPricingLink';
import MissingPriceItem from './MissingPriceItem';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { skippedReasonText } from '../../utils/registrationCreditOutcome';
import { OpenLinksInNewTabProvider } from './linkTarget';
import type { BulkCheckResult, FamilyRef, GenerateInvoicesResult, ManualEntry } from '../../api/invoices.api';

type Props =
  | { mode: 'check'; report: BulkCheckResult; getFamilyName: (familyId: string) => string }
  | { mode: 'result'; report: GenerateInvoicesResult; getFamilyName: (familyId: string) => string };

/** A count with the names behind a "Show" toggle: the families need nothing, but can be seen. */
function QuietLine({ text, families }: { text: string; families: Array<FamilyRef & { extra?: string }> }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {text}{' '}
        <Button
          size="small"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline' }}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Typography>
      {open && (
        <Typography variant="caption" color="text.secondary" component="div">
          {families.map((f) => (f.extra ? `${f.familyName} (${f.extra})` : f.familyName)).join(', ')}
        </Typography>
      )}
    </Box>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export default function BulkRunReport(props: Props) {
  const { pathname } = useLocation();
  const schoolSlug = extractSchoolSlugFromPath(pathname);
  const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';
  const { mode, report, getFamilyName } = props;

  // Older servers send none of the #376 lists: read every one defensively.
  const missing = report.fixes ?? [];
  const manual: ManualEntry[] = report.manual ?? [];
  const alreadyInvoiced = report.alreadyInvoiced ?? [];
  const nothingToBill = report.nothingToBill ?? [];
  const notBilled = report.notBilled ?? [];
  const errors = report.errors ?? [];
  const created = props.mode === 'result' ? props.report.created ?? [] : [];
  const willCreate = props.mode === 'check' ? props.report.willCreate : [];
  // #374: the registration fee credits the run applied (or Check first says it would apply).
  const credits = report.registrationCredits?.applied ?? [];
  // Held fees the run could not credit (another invoice took it, or nothing was left): never silent.
  const creditsSkipped = report.registrationCredits?.skipped ?? [];

  const nothingAtAll =
    created.length === 0 &&
    willCreate.length === 0 &&
    missing.length === 0 &&
    manual.length === 0 &&
    alreadyInvoiced.length === 0 &&
    nothingToBill.length === 0 &&
    notBilled.length === 0 &&
    credits.length === 0 &&
    creditsSkipped.length === 0 &&
    errors.length === 0;

  // Every link in the report opens in a NEW tab (W1): following one must not lose the report.
  return (
    <OpenLinksInNewTabProvider value>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* 1. What the run did / is expected to do */}
      {mode === 'result' && created.length > 0 && (
        <Box>
          <Alert severity="success" sx={{ mb: 1 }}>
            Successfully created {created.length} invoice(s)
          </Alert>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {created.map((invoice) => (
              <ListItem key={invoice.id}>
                <ListItemText
                  primary={invoice.invoiceNumber}
                  secondary={`${invoice.Family?.name ?? getFamilyName(invoice.familyId)} - $${Number(invoice.total).toFixed(2)}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      {mode === 'check' && (
        <Alert severity={willCreate.length > 0 ? 'info' : 'warning'}>
          Expected: {plural(willCreate.length, 'family', 'families')} will be invoiced. Nothing is created until you
          press Generate Invoices; a discount that cannot be applied only shows after the run.
        </Alert>
      )}

      {/* 1b. #374: registration fee credits (a credit off the child's tuition, not a refund) */}
      {credits.length > 0 && (
        <Box data-testid="registration-credits">
          <Alert severity="info" sx={{ mb: 1 }}>
            {mode === 'result'
              ? `${plural(credits.length, 'registration fee credit', 'registration fee credits')} applied`
              : `${plural(credits.length, 'registration fee credit', 'registration fee credits')} will be applied`}
          </Alert>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {credits.map((c) => (
              <ListItem key={`${c.familyId}-${c.studentId}`} sx={{ py: 0 }}>
                <ListItemText
                  primary={`${c.studentName} · ${c.familyName}`}
                  secondary={`-$${Number(c.amount).toFixed(2)}${c.invoiceNumber ? ` on ${c.invoiceNumber}` : ''}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {creditsSkipped.length > 0 && (
        <Box data-testid="registration-credits-skipped">
          <Alert severity="warning" sx={{ mb: 1 }}>
            {plural(creditsSkipped.length, 'registration fee credit', 'registration fee credits')} not applied: the fee stays held and is on
            the &quot;Registration fees not yet applied&quot; report.
          </Alert>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {creditsSkipped.map((c, i) => (
              <ListItem key={`${c.familyId}-${c.studentName}-${i}`} sx={{ py: 0 }}>
                <ListItemText
                  primary={`${c.studentName} · ${c.familyName}`}
                  secondary={`${skippedReasonText(c.reason)}${c.invoiceNumber ? ` (${c.invoiceNumber})` : ''}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* 2. The fixes, each naming the families it holds back */}
      {missing.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {mode === 'result' ? 'To finish this run, fix these' : 'Fix these first, or these families will be held back'}
            {' '}({plural(new Set(missing.flatMap((m) => m.familyIds)).size, 'family', 'families')} held back):
          </Typography>
          <List dense disablePadding sx={{ mb: 0.5 }}>
            {missing.map((m) => (
              <ListItem key={`${m.lessonId}-${m.durationMinutes}-${m.category}`} sx={{ py: 0, display: 'block' }}>
                <ListItemText primary={<MissingPriceItem missing={m} />} />
                <Typography variant="caption" color="text.secondary" component="div">
                  Holding back: {m.familyNames.join(', ')}
                </Typography>
              </ListItem>
            ))}
          </List>
          {missing.some((m) => !m.noProgramLevel) && <ClassPricingLink />}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {mode === 'result'
              ? 'Then run Auto Generate again to bill these families.'
              : 'You can also generate now and run Auto Generate again after fixing them.'}
          </Typography>
        </Alert>
      )}

      {/* 3. Do these by hand (the asterisk) */}
      {manual.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            * Do these by hand
          </Typography>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            {manual.map((m, i) => (
              <ListItem key={`${m.familyId}-${m.reason}-${m.enrollmentId ?? i}`} sx={{ display: 'block', py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  * {m.familyName}
                </Typography>
                <Typography variant="body2">{m.detail}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {m.remedy}
                  {m.invoiceId && (
                    <>
                      {' '}
                      <Link component={RouterLink} to={`${slugPrefix}/admin/invoices/${m.invoiceId}`} target="_blank" rel="noopener">
                        Open {m.invoiceNumber ?? 'invoice'}
                      </Link>
                    </>
                  )}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* 4. Children left out (#190): marked Inactive or removed — never silent */}
      {notBilled.length > 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            {notBilled.length === 1
              ? '1 enrolment not billed: the child is marked Inactive or removed.'
              : `${notBilled.length} enrolments not billed: the child is marked Inactive or removed.`}
            {/* Only an Inactive child can simply be set back; a removed one cannot (QA L4). */}
            {notBilled.some((row) => row.reason === 'STUDENT_INACTIVE') &&
              ' If a child marked Inactive still attends, set them back to Active on their student page, then generate again.'}
          </Alert>
          <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {notBilled.map((row) => (
              <ListItem key={row.enrollmentId} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${row.studentName} · ${row.lessonName}`}
                  secondary={`${row.familyName} · ${row.reason === 'STUDENT_INACTIVE' ? 'marked Inactive' : 'removed'}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* 5. Quiet lines: nothing to do */}
      {alreadyInvoiced.length > 0 && (
        <QuietLine
          text={`${plural(alreadyInvoiced.length, 'family', 'families')} already invoiced, nothing new to bill.`}
          families={alreadyInvoiced.map((f) => ({ ...f, extra: f.invoiceNumbers.join(', ') }))}
        />
      )}
      {nothingToBill.length > 0 && (
        <QuietLine
          text={`${plural(nothingToBill.length, 'family', 'families')} had nothing to bill this term.`}
          families={nothingToBill}
        />
      )}

      {/* 6. Real failures */}
      {errors.length > 0 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {errors.length} family/families not invoiced
          </Alert>
          <List dense sx={{ bgcolor: 'error.light', borderRadius: 1, p: 1 }}>
            {errors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={getFamilyName(error.familyId)}
                  secondary={error.error}
                  secondaryTypographyProps={{ color: 'error.dark' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {nothingAtAll && (
        <Alert severity="info">
          No families found with active enrolments for this term. Make sure:
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Students are enrolled in lessons for the selected term</li>
            <li>Students are assigned to families</li>
            <li>Enrolments are marked as active</li>
          </ul>
        </Alert>
      )}
    </Box>
    </OpenLinksInNewTabProvider>
  );
}
